import { useAuth } from "@clerk/react";
import { Plus, Sparkles } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { isPlusPurchaseEnabled } from "@sill/shared/components/billing/pricing-copy";

import { useEntitlements } from "../../hooks/useEntitlements";
import { cn } from "../../lib/utils";
import { PlusBadge } from "./PlusBadge";
import { UpgradeSheet } from "./UpgradeSheet";

export interface SidebarPlusBadgeButtonProps {
  // Renders the badge in a smaller-footprint mode for the 76px sidebar rail.
  compact?: boolean;
  className?: string;
}

// SidebarPlusBadgeButton lives in the desktop sidebar footer between the
// Settings cog and the AvatarBadge (see DashboardPage.tsx desktop sidebar).
// Behavior:
//   - Loading (no cached entitlement)         → render nothing (anti-flicker).
//   - Plus active (current profile or family) → solid green PLUS badge;
//     click jumps to /dashboard/settings#subscription.
//   - Free                                    → outlined "Upgrade" pill;
//     click opens UpgradeSheet directly (no Settings detour — the modal
//     IS the comparison surface now).
//
// Two display modes:
//   - Default (expanded sidebar): word-based pill ("PLUS" / "✦ Upgrade").
//   - `compact=true` (76px rail): icon-only circle sized to match the
//     adjacent gear + avatar so the footer column stays balanced. "Upgrade"
//     as a word overflows the rail at this width; rendering it as a chip
//     made the whole column read uneven (the 2026-05-18 review).
export function SidebarPlusBadgeButton({
  className,
  compact = false,
}: SidebarPlusBadgeButtonProps): ReactNode {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { isLoading, isPlus } = useEntitlements({ getToken });
  const [open, setOpen] = useState(false);

  if (isLoading) return null;
  // Purchase kill-switch: hide the upgrade CTA entirely for free users when
  // SILL_PLUS_PURCHASE_ENABLED=false. Don't tease an upgrade we can't
  // currently deliver. Plus users keep the badge so they can manage their
  // existing subscription (cancel / change card flow stays open).
  if (!isPlus && !isPlusPurchaseEnabled()) return null;

  const handleClick = () => {
    if (isPlus) {
      navigate("/dashboard/settings#subscription");
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button
        aria-label={isPlus ? "Manage Plus subscription" : "Upgrade to Plus"}
        className={cn(
          "inline-flex items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          "hover:opacity-90 active:scale-[0.97]",
          // In compact mode, give the button the same h-9 w-9 hit-area
          // as the gear icon above it so all three column items (gear,
          // chip, avatar) share a vertical axis. The h-7 w-7 chip then
          // sits centered inside, giving it air without making it the
          // loudest element in the footer.
          compact && "h-9 w-9",
          className,
        )}
        data-compact={compact ? "true" : "false"}
        onClick={handleClick}
        type="button"
      >
        {compact ? (
          <CompactChip variant={isPlus ? "active" : "upgrade"} />
        ) : isPlus ? (
          <PlusBadge size="sm" variant="active" />
        ) : (
          <UpgradeChip />
        )}
      </button>
      <UpgradeSheet
        onClose={() => setOpen(false)}
        open={open}
        reason="pricing_page"
      />
    </>
  );
}

// UpgradeChip is the Free-state sidebar pill (expanded sidebar only).
// Visually distinct from the active Plus chip: subtle card background,
// primary-tinted text, a small sparkle glyph. The `data-variant="upgrade"`
// hook lets tests assert the Free-state styling without depending on
// classnames.
function UpgradeChip(): ReactNode {
  return (
    <span
      aria-label="Upgrade to Plus"
      data-variant="upgrade"
      className="inline-flex min-h-6 items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-primary"
    >
      <Sparkles className="h-2.5 w-2.5" />
      Upgrade
    </span>
  );
}

// CompactChip is the icon-only badge rendered on the 76px rail. h-7 w-7
// circle (sized to sit visually between the gear icon above and the
// avatar below — neither dominates the column). The fill state encodes
// Plus vs Free — solid green = active, outlined primary = upgrade —
// matching the colour language of the expanded chips above. The active
// glyph is a plain "+" because the product is literally Sill Plus; the
// upgrade glyph is a sparkle to read as "premium tease".
function CompactChip({
  variant,
}: {
  variant: "active" | "upgrade";
}): ReactNode {
  const isActive = variant === "active";
  return (
    <span
      aria-hidden
      data-variant={variant}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-full",
        isActive
          ? "bg-primary text-primary-foreground"
          : "border border-primary/40 bg-primary/5 text-primary",
      )}
    >
      {isActive ? (
        <Plus className="h-3.5 w-3.5" strokeWidth={3} />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
    </span>
  );
}
